import { GoogleGenerativeAI } from "@google/generative-ai";
import { supabase, getPublicUrl } from '../supabaseClient'; // Assuming supabaseClient exports getPublicUrl

// --- Utility Functions (Adapted from DocumentGenerator) ---

const base64ToBlob = (base64, mimeType) => {
  // Handle data URLs
  let base64Data = base64;
  if (base64.startsWith('data:')) {
    base64Data = base64.split(',')[1];
  }

  try {
    const byteCharacters = atob(base64Data);
    const byteArrays = [];

    for (let i = 0; i < byteCharacters.length; i += 1024) {
      const slice = byteCharacters.slice(i, i + 1024);
      const byteNumbers = new Array(slice.length);
      for (let j = 0; j < slice.length; j++) {
        byteNumbers[j] = slice.charCodeAt(j);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, { type: mimeType });
  } catch (e) {
    console.error("Error in base64ToBlob:", e);
    // Handle potential malformed base64 strings
    if (e instanceof DOMException && e.name === 'InvalidCharacterError') {
      console.error("Invalid character found in base64 string.");
      // Attempt to clean the string (basic example, might need more robust cleaning)
      const cleanedBase64 = base64Data.replace(/[^A-Za-z0-9+/=]/g, '');
      try {
        // Retry with cleaned string
        const byteCharactersRetry = atob(cleanedBase64);
        const byteArraysRetry = [];
        for (let i = 0; i < byteCharactersRetry.length; i += 1024) {
          const slice = byteCharactersRetry.slice(i, i + 1024);
          const byteNumbers = new Array(slice.length);
          for (let j = 0; j < slice.length; j++) {
            byteNumbers[j] = slice.charCodeAt(j);
          }
          const byteArray = new Uint8Array(byteNumbers);
          byteArraysRetry.push(byteArray);
        }
        return new Blob(byteArraysRetry, { type: mimeType });
      } catch (retryError) {
        console.error("Retry with cleaned base64 failed:", retryError);
        return null;
      }
    }
    return null; // Return null or throw error as appropriate
  }
};


/**
 * Analyzes an image using Gemini to suggest potential edits.
 *
 * @param {string|File} inputImage - The input image URL or File object to analyze.
 * @param {string} [contextPrompt=""] - Optional additional context (e.g., room type).
 * @param {function} [onProgress] - Optional callback for progress updates.
 * @returns {Promise<string[]>} - A promise that resolves to an array of suggested edit prompt strings.
 */
export const analyzeImageForEdits = async (inputImage, contextPrompt = "", onProgress = () => {}) => {
  if (!textModel) { // Use the text/vision model for analysis
    throw new Error("Gemini text/vision model not initialized. Cannot analyze image.");
  }
  if (!inputImage) {
    throw new Error("Input image is required for analysis.");
  }

  console.log("Starting Gemini Image Analysis for Edits...");
  onProgress(0.1, null, "Preparing image for analysis...");

  try {
    // --- Process Input Image ---
    let inputMimeType = null;
    let inputBase64Data = null;
    onProgress(0.2, null, "Processing image...");
    try {
      let imageBlob;
      if (inputImage instanceof File) {
        imageBlob = inputImage;
        inputMimeType = inputImage.type;
      } else if (typeof inputImage === 'string') {
        const { blob: fetchedBlob, mimeType: fetchedMimeType } = await urlToBlob(inputImage);
        imageBlob = fetchedBlob;
        inputMimeType = fetchedMimeType;
      } else {
        throw new Error('Invalid input image type.');
      }
      if (!imageBlob) throw new Error('Failed to get image blob.');

      inputBase64Data = await blobToBase64(imageBlob);
      onProgress(0.4, null, "Image ready for analysis.");
    } catch (error) {
      console.error("Error processing input image for analysis:", error);
      throw new Error(`Failed to process input image: ${error.message}`);
    }

    // --- Construct Analysis Prompt ---
    const analysisPrompt = `
Analyze the provided image ${contextPrompt ? `of a ${contextPrompt}` : ''}.
Identify key objects and areas suitable for editing.
Suggest 4 diverse and specific edits that could be made using natural language instructions IN TURKISH.
Focus on actions like adding relevant objects, removing existing objects, changing colors/materials of specific items, or modifying distinct areas (like the floor or a wall).
Format the response ONLY as a valid JSON array of strings, where each string is a complete edit instruction IN TURKISH, ready to be used.
Example JSON response: ["Mavi koltuğu kaldır", "Pencerenin yanına uzun saksı bitkisi ekle", "Duvar rengini açık gri yap", "Halıyı desenli bir halıyla değiştir"]
Do not include any other text, explanations, or markdown formatting outside the JSON array. Respond ONLY in the requested JSON format.
`;

    const contentParts = [
      { text: analysisPrompt },
      { inlineData: { mimeType: inputMimeType, data: inputBase64Data } }
    ];

    // --- Call Gemini API ---
    onProgress(0.5, null, "Analyzing image with AI...");
    console.log("Sending analysis request to Gemini...");

    // Use a reasonable timeout for analysis
    const analysisTimeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`Timeout: Analysis exceeded ${GENERATION_TIMEOUT_MS / 2000} seconds.`)), GENERATION_TIMEOUT_MS / 2) // Shorter timeout for analysis?
    );

    const analysisPromise = textModel.generateContent({
        contents: [{ role: "user", parts: contentParts }],
        generationConfig: {
            // Adjust temperature for analysis - maybe slightly higher for diversity?
            temperature: 0.5,
            topK: 40,
            topP: 0.95,
            // responseMimeType: "application/json", // Request JSON directly if model supports
        },
        // Safety settings inherited
    });

    const result = await Promise.race([analysisPromise, analysisTimeoutPromise]);
    onProgress(0.8, null, "Processing analysis results...");

    const response = result.response;
    const responseText = response?.candidates?.[0]?.content?.parts?.[0]?.text?.trim();

    if (!responseText) {
      console.error("Invalid or empty response structure from Gemini analysis:", response);
      throw new Error("Invalid response received from analysis API.");
    }

    console.log("Raw analysis response text:", responseText);

    // --- Parse JSON Response ---
    try {
      // Attempt to clean the response text before parsing
      // Find the start and end of the JSON array
      const jsonStart = responseText.indexOf('[');
      const jsonEnd = responseText.lastIndexOf(']');
      
      if (jsonStart === -1 || jsonEnd === -1 || jsonEnd < jsonStart) {
          throw new Error("Could not find valid JSON array brackets in the response.");
      }
      
      const jsonString = responseText.substring(jsonStart, jsonEnd + 1);
      console.log("Extracted JSON string:", jsonString);

      const suggestions = JSON.parse(jsonString);
      if (!Array.isArray(suggestions) || !suggestions.every(s => typeof s === 'string')) {
        throw new Error("Parsed JSON is not an array of strings.");
      }
      console.log("Parsed suggestions:", suggestions);
      onProgress(1.0, suggestions, "Analysis complete.");
      return suggestions;
    } catch (parseError) {
      console.error("Failed to parse JSON response from analysis:", parseError);
      console.error("Response text that failed parsing:", responseText);
      throw new Error(`AI Analizi yorumlanamadı. Lütfen tekrar deneyin veya manuel düzenlemeyi kullanın. (Hata: ${parseError.message})`);
    }

  } catch (error) {
    console.error('Gemini Image Analysis Error:', error);
    onProgress(1.0, null, `Error: ${error.message}`);
    throw error; // Re-throw the error
  }
};



const resizeImage = (fileOrUrl, maxWidth = 1344, outputFormat = 'image/webp', quality = 0.9) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous"; // Handle CORS for URLs

    img.onload = () => {
      const canvas = document.createElement('canvas');
      let { width, height } = img;

      // Calculate new dimensions while maintaining aspect ratio
      if (width > maxWidth) {
        const ratio = maxWidth / width;
        width = maxWidth;
        height *= ratio;
      }
      // Optional: Add maxHeight constraint if needed
      // if (height > maxHeight) { ... }

      console.log(`Resizing image to ${width}x${height}`);
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      // Convert canvas to blob in the desired format
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Canvas to Blob conversion failed'));
        }
      }, outputFormat, quality);
    };

    img.onerror = (error) => {
      console.error("Image loading error:", error, "Source was:", img.src); // Add img.src to log
      reject(new Error('Failed to load image for resizing. Check URL/CORS policy.'));
    };

    // Set the source (works for both File objects and URLs)
    if (fileOrUrl instanceof File) {
      const reader = new FileReader();
      reader.onload = (event) => { img.src = event.target.result; };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(fileOrUrl);
    } else if (typeof fileOrUrl === 'string') {
      if (fileOrUrl.startsWith('blob:')) {
        // Handle blob URLs directly
        img.src = fileOrUrl; // <-- Use the blob URL directly
      } else {
        // Handle regular HTTP(S) URLs
        // Add timestamp to bypass cache for URLs
        try { // Add try-catch for URL parsing
            const url = new URL(fileOrUrl);
            url.searchParams.set('t', Date.now());
            img.src = url.toString();
        } catch (e) {
            // Handle potential invalid URLs passed as strings
            console.error("Invalid URL string passed to resizeImage:", fileOrUrl, e);
            reject(new Error(`Invalid URL string for resizing: ${fileOrUrl}`));
        }
      }
    } else {
      reject(new Error('Invalid input type for resizeImage. Must be File or URL string.'));
    }
  });
};


const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      // Ensure result is a string and split correctly
      const base64String = reader.result;
      if (typeof base64String === 'string' && base64String.includes(',')) {
        resolve(base64String.split(',')[1]); // Return only the base64 data part
      } else {
        reject(new Error('Failed to read blob as base64 data URL.'));
      }
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(blob);
  });
};

// Helper to fetch a URL or data URL and return a Blob with its mime type
const urlToBlob = async (url) => {
  try {
    // Check if the input is a data URL
    if (url.startsWith('data:')) {
      console.log("Processing data URL...");
      const parts = url.split(',');
      const mimeMatch = parts[0].match(/:(.*?);/);
      const mimeType = mimeMatch ? mimeMatch[1] : 'image/png';
      const base64Data = parts[1];
      
      // Convert base64 to blob
      const byteCharacters = atob(base64Data);
      const byteArrays = [];
      
      for (let i = 0; i < byteCharacters.length; i += 1024) {
        const slice = byteCharacters.slice(i, i + 1024);
        const byteNumbers = new Array(slice.length);
        for (let j = 0; j < slice.length; j++) {
          byteNumbers[j] = slice.charCodeAt(j);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }
      
      const blob = new Blob(byteArrays, { type: mimeType });
      console.log("Processed data URL to blob. Type:", mimeType, "Size:", blob.size);
      return { blob, mimeType };
    }
    
    // Handle regular URLs
    // Add timestamp to bypass cache for URLs
    const urlObj = new URL(url);
    urlObj.searchParams.set('t', Date.now());
    const fetchUrl = urlObj.toString();

    console.log("Fetching URL:", fetchUrl);
    const response = await fetch(fetchUrl, { cache: 'no-store' }); // Try to prevent caching
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
    }
    const blob = await response.blob();
    console.log("Fetched blob type:", blob.type, "size:", blob.size);
    // Try to infer mime type if blob.type is empty (common for some storage providers)
    let mimeType = blob.type;
    if (!mimeType) {
        const extension = url.split('.').pop().toLowerCase().split('?')[0]; // Get extension before query params
        if (extension === 'jpg' || extension === 'jpeg') mimeType = 'image/jpeg';
        else if (extension === 'png') mimeType = 'image/png';
        else if (extension === 'webp') mimeType = 'image/webp';
        else if (extension === 'gif') mimeType = 'image/gif';
        // Add more mappings if needed
        console.log(`Inferred mime type from extension '.${extension}': ${mimeType}`);
    }

    if (!mimeType) {
        console.warn("Could not determine mime type for fetched URL:", url);
        // Default to jpeg or throw error? Let's default for now.
        mimeType = 'image/jpeg';
    }

    return { blob, mimeType };
  } catch (error) {
    console.error("Error fetching URL to blob:", error);
    throw error;
  }
};

// Style attributes mapping for CanvasEditor workflows (renamed)
const canvasStyleAttributes = {
  'scandinavian': 'light-colored wood, soft textures, functional, minimalist, natural materials, white and light tones',
  'contemporary': 'clean lines, neutral colors, metal or glass details, modern, sleek surfaces',
  'modern': 'simple forms, functional, neutral palette, geometric patterns, uncluttered',
  'minimalist': 'simple, functional, few items, ample empty space, neutral colors, clean lines',
  'bohemian': 'colorful, patterned, layered textures, plants, macrame, relaxed, eclectic',
  'japandi': 'Scandinavian simplicity and Japanese naturalness, wood, bamboo, calm colors, zen-like',
  'parisian': 'elegant, cream or pastel tones, curved lines, vintage-inspired, ornate details',
  'eclectic': 'mix of different periods and styles, bold colors or patterns, unique designs, personalized'
  // Add more styles if needed
};

// Furniture mapping by room type (needed for detailed prompt)
const furnitureByRoomType = {
  'Living Room': ['sofa', 'coffee table', 'rug', 'armchair', 'bookshelf', 'TV stand', 'canvas painting', 'floor lamp', 'side table'],
  'Bedroom': ['bed', 'nightstand', 'dresser', 'wardrobe', 'rug', 'lamp', 'canvas painting', 'mirror', 'chair'],
  'Kitchen': ['cabinets', 'countertop', 'island', 'stools', 'sink', 'oven', 'refrigerator', 'plant', 'pendant lights', 'open shelving'],
  'Bathroom': ['vanity with sink', 'mirror', 'shower', 'bathtub', 'toilet', 'towel rack', 'bath mat', 'wall cabinet', 'shelf'],
  'Dining Room': ['dining table', 'chairs', 'sideboard', 'chandelier', 'rug', 'artwork', 'canvas painting', 'console table', 'bar cart']
  // Add mappings for other room types if needed from canvasWorkflows.js
};

// Placement guidelines by room type to maintain spaciousness
const placementGuidelinesByRoomType = {
  'Living Room': 'Position furniture to maintain open pathways. Keep the center area open for movement.',
  'Bedroom': 'Ensure there is space around the bed. Position furniture against walls when possible.',
  'Kitchen': 'Maintain the kitchen work triangle. Keep walkways clear.',
  'Bathroom': 'Ensure fixtures are appropriately scaled for the room size. Maintain clear floor space.',
  'Dining Room': 'Center the dining table in the space. Allow room for chair movement.'
};


// --- Gemini API Service ---

const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
// Use the specific image generation model and include safety settings during initialization
const imageModel = genAI.getGenerativeModel({
  model: "gemini-2.0-flash-exp-image-generation",
  location: "europe-west3", // Specify the location
  safetySettings: [
    { category: "HARM_CATEGORY_HARASSMENT", threshold: "BLOCK_NONE" },
    { category: "HARM_CATEGORY_HATE_SPEECH", threshold: "BLOCK_NONE" },
    { category: "HARM_CATEGORY_SEXUALLY_EXPLICIT", threshold: "BLOCK_NONE" },
    { category: "HARM_CATEGORY_DANGEROUS_CONTENT", threshold: "BLOCK_NONE" }
  ]
});

// Initialize the text model specifically for translation tasks
let textModel;
try {
  if (process.env.REACT_APP_GEMINI_API_KEY) {
    const textGenAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);
    textModel = textGenAI.getGenerativeModel({ model: "gemini-1.5-flash-latest" });
  } else {
    console.error("Gemini API Key not found. Translation features will be disabled.");
  }
} catch (error) {
  console.error("Error initializing Gemini Text API:", error);
  textModel = null; // Ensure model is null if initialization fails
}

/**
 * Translates text to English using the Gemini API.
 *
 * @param {string} text - The text to translate.
 * @returns {Promise<string>} - The translated English text.
 * @throws {Error} If translation fails or the API is not initialized.
 */
export const translateTextWithGemini = async (text) => {
  if (!textModel) {
    throw new Error("Gemini text model not initialized. Cannot translate.");
  }
  if (!text || typeof text !== 'string') {
    return text; // Return original text if empty or not a string
  }

  console.log("Translating text with Gemini:", text);
  try {
    // Simple prompt for direct translation
    const prompt = `Translate the following text to English. Respond ONLY with the translated text, without any introductory phrases, explanations, or quotation marks:\n\n"${text}"`;

    const result = await textModel.generateContent(prompt);
    const response = result.response;

    if (response?.candidates?.[0]?.content?.parts?.[0]?.text) {
      let translated = response.candidates[0].content.parts[0].text.trim();
      // Clean up potential quotes added by the model (just in case)
      if (translated.startsWith('"') && translated.endsWith('"')) {
        translated = translated.substring(1, translated.length - 1);
      }
      console.log("Translation successful:", translated);
      return translated;
    } else {
      console.error("Invalid response structure from Gemini translation:", response);
      throw new Error("Invalid response received from translation API.");
    }
  } catch (error) {
    console.error("Gemini translation error:", error);
    // Throw a more specific error or the original one
    throw new Error(`Translation failed: ${error.message}`);
  }
};


/**
 * Generates an image using Gemini based on a prompt and an optional input image.
 * Uploads the result to Supabase and returns the public URL.
 *
 * @param {string} prompt - The text prompt for image generation.
 * @param {string|File} [inputImage] - Optional input image URL or File object.
 * @param {object} [options] - Additional options (e.g., style, roomType).
 * @param {function} [onProgress] - Optional callback for progress updates (value 0-1, output, message).
 * @returns {Promise<{publicUrl: string, imageGenPrompt: string}>} - An object containing the public Supabase URL and the prompt used for generation.
 */
const GENERATION_TIMEOUT_MS = 25000; // 25 seconds
const MAX_RETRIES = 3;

export const runGeminiImageGeneration = async (prompt, inputImage = null, options = {}, onProgress = () => {}) => {
  console.log("Starting Gemini Image Generation...");
  onProgress(0.1, null, "Preparing input..."); // Initial progress

  let generatedImageUrl = null; // Variable to store the final URL
  let lastError = null;

  try {
    // --- Construct a simplified prompt listing furniture ---
    // Extract options with defaults
    const {
        roomType = 'Living Room',
        style = 'contemporary',
        furnitureColor = 'original', // Default to 'original' if not provided
        isRoomFurnished = false
    } = options;
    console.log("Received options for prompt:", { roomType, style, furnitureColor, isRoomFurnished }); // Log received options

    // Get furniture list for the room type, default to Living Room if not found
    const furnitureList = furnitureByRoomType[roomType] || furnitureByRoomType['Living Room'];
    
    // Construct the furniture list string
    const furnitureItemsString = furnitureList.length > 0 ? `such as ${furnitureList.join(', ')}` : '';

    // Determine the color hint. Only add if it's not 'original'.
    const colorHint = (furnitureColor && furnitureColor !== 'original') ? `predominantly ${furnitureColor}, ` : '';
    console.log("Color Hint:", colorHint || "None (original)"); // Log the color hint being used

    // Combine into the final prompt
    // Prepend prefix if the room is marked as furnished
    const prefix = isRoomFurnished ? "First, clear the furniture in the room. Then, " : "";

    // Construct a simplified, direct prompt following the example structure
    const imageGenPrompt = `Edit this image of ${roomType}. Add ${style} ${roomType.toLowerCase()} furniture: ${furnitureItemsString}. ${colorHint ? `Use ${colorHint} colors. ` : ''}`;

    console.log("Constructed Prompt:", imageGenPrompt);

    const contentParts = [{ text: imageGenPrompt }];

    // --- Process Input Image (No Resizing) ---
    let inputMimeType = null;
    let inputBase64Data = null;

    if (inputImage) {
      onProgress(0.2, null, "Processing input image...");
      try {
        let imageBlob;
        if (inputImage instanceof File) {
          console.log("Processing File input...");
          imageBlob = inputImage;
          inputMimeType = inputImage.type;
        } else if (typeof inputImage === 'string') {
          console.log("Processing string input (URL or data URL)...");
          const { blob: fetchedBlob, mimeType: fetchedMimeType } = await urlToBlob(inputImage);
          imageBlob = fetchedBlob;
          inputMimeType = fetchedMimeType;
        } else {
          throw new Error('Invalid input image type. Must be File or URL string.');
        }

        if (!imageBlob) {
            throw new Error('Failed to get image blob.');
        }

        console.log(`Input image details - MimeType: ${inputMimeType}, Size: ${imageBlob.size} bytes`);
        console.log("Converting original image to base64...");
        inputBase64Data = await blobToBase64(imageBlob);
        contentParts.push({
          inlineData: {
            mimeType: inputMimeType, // Use original mime type
            data: inputBase64Data,   // Use the correct variable
          },
        });
        console.log("Input image processed successfully.");
        onProgress(0.4, null, "Input image ready.");
      } catch (error) {
        console.error("Error processing input image:", error);
        throw new Error(`Failed to process input image: ${error.message}`);
      }
    } else {
        onProgress(0.4, null, "No input image provided.");
    }

    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      console.log(`Gemini Image Generation Attempt ${attempt}/${MAX_RETRIES}...`);
      const attemptProgressStart = 0.5 + (0.4 / MAX_RETRIES) * (attempt - 1); // Progress range 0.5 -> 0.9
      const attemptProgressEnd = attemptProgressStart + (0.4 / MAX_RETRIES);

      try {
        // --- Call Gemini API with Timeout ---
        onProgress(attemptProgressStart, null, `Generating image (Attempt ${attempt})...`);
        console.log("Sending request to Gemini API with content parts:", contentParts);

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`Timeout: Generation exceeded ${GENERATION_TIMEOUT_MS / 1000} seconds.`)), GENERATION_TIMEOUT_MS)
        );

        const generationPromise = imageModel.generateContent({
          contents: [{ role: "user", parts: contentParts }],
          generationConfig: {
            responseModalities: ['Text', 'Image'],
            temperature: 0.5,
            topK: 1,
            topP: 0.95,
          },
          // Safety settings inherited from model initialization
        });

        // Race the generation against the timeout
        const result = await Promise.race([generationPromise, timeoutPromise]);

        onProgress(attemptProgressEnd * 0.8, null, "Processing Gemini response..."); // Progress within attempt
        console.log("Received response from Gemini API.");

        const response = result.response;
        if (!response || !response.candidates || response.candidates.length === 0) {
          console.error("Invalid response structure from Gemini:", response);
          throw new Error("Invalid or empty response received from Gemini API.");
        }

        const candidate = response.candidates[0];
        if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
          console.error("No content parts found in Gemini response:", candidate);
          throw new Error("No content parts found in the Gemini response.");
        }

        const imagePart = candidate.content.parts.find(part => part.inlineData && part.inlineData.data);

        if (!imagePart) {
          const textPart = candidate.content.parts.find(part => part.text);
          const errorMessage = textPart ? `Gemini Error: ${textPart.text}` : "No image data found in the Gemini response.";
          console.error(errorMessage, candidate.content.parts);
          throw new Error(errorMessage);
        }

        const generatedBase64 = imagePart.inlineData.data;
        const generatedMimeType = imagePart.inlineData.mimeType || 'image/webp';

        console.log(`Generated image mime type: ${generatedMimeType}`);
        onProgress(attemptProgressEnd * 0.9, null, "Uploading generated image..."); // Progress before upload

        // --- Convert Base64 to Blob ---
        const generatedBlob = base64ToBlob(generatedBase64, generatedMimeType);
        if (!generatedBlob) {
          throw new Error("Failed to convert generated base64 to Blob.");
        }

        // --- Ensure Blob is WebP for upload ---
        let uploadBlob = generatedBlob;
        let uploadMimeType = generatedMimeType;
        const desiredUploadFormat = 'image/webp';

        if (generatedMimeType !== desiredUploadFormat && generatedBlob) {
          console.log(`Converting generated ${generatedMimeType} to ${desiredUploadFormat}...`);
          let objectUrl = null; // Variable to hold the temporary object URL
          try {
            // Create an object URL from the blob to pass to resizeImage
            objectUrl = URL.createObjectURL(generatedBlob);
            const webpBlob = await resizeImage(objectUrl, 4096, desiredUploadFormat, 0.9); // Pass the object URL
            uploadBlob = webpBlob;
            uploadMimeType = desiredUploadFormat;
            console.log("Conversion to webp successful.");
          } catch (conversionError) {
            console.warn(`Failed to convert to ${desiredUploadFormat}. Uploading as ${generatedMimeType}. Error: ${conversionError.message}`);
            uploadBlob = generatedBlob; // Keep original blob if conversion fails
            uploadMimeType = generatedMimeType; // Keep original mime type if conversion fails
          } finally {
            // Revoke the object URL to free up memory, regardless of success or failure
            if (objectUrl) {
              URL.revokeObjectURL(objectUrl);
              console.log("Revoked temporary object URL.");
            }
          }
        }

        // --- Upload to Supabase ---
        const { data: { user } } = await supabase.auth.getUser();
        const userId = user?.id || 'anonymous';
        const fileName = `${userId}/gemini-${Date.now()}.webp`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('generated-images')
          .upload(fileName, uploadBlob, {
            contentType: uploadMimeType,
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          console.error("Supabase upload error:", uploadError);
          throw new Error(`Failed to upload generated image: ${uploadError.message}`);
        }

        console.log("Image uploaded to Supabase:", uploadData.path);

        // --- Get Public URL ---
        const publicUrl = getPublicUrl('generated-images', fileName);
        if (!publicUrl) {
          throw new Error("Failed to get public URL for the uploaded image.");
        }

        console.log("Generated image public URL:", publicUrl);
        generatedImageUrl = publicUrl;
        onProgress(1.0, { publicUrl, imageGenPrompt }, "Generation complete!"); // Final success progress
        return { publicUrl, imageGenPrompt }; // Success, return URL and prompt

      } catch (error) {
        lastError = error; // Store the error from this attempt
        console.error(`Gemini Image Generation Attempt ${attempt} failed:`, error);

        if (attempt < MAX_RETRIES) {
          onProgress(attemptProgressEnd, null, `Attempt ${attempt} failed. Retrying... (${error.message})`);
          // Optional: Add a small delay before retrying
          // await new Promise(resolve => setTimeout(resolve, 1000));
        } else {
          // Max retries reached
          console.error("Max retries reached. Generation failed.");
          onProgress(1.0, null, `Error: Generation failed after ${MAX_RETRIES} attempts. Please try again.`);
          // Throw a user-friendly error after exhausting retries
          throw new Error(`Görsel oluşturma ${MAX_RETRIES} denemeden sonra başarısız oldu. Lütfen tekrar deneyin veya farklı bir stil/oda seçin. (Son hata: ${lastError.message})`);
        }
      }
    }
  } catch (error) {
      // Catch errors from the initial setup phase (before the loop) or the final re-thrown error
      console.error('Gemini Image Generation Error (Overall):', error);
      // Ensure progress is set to 1.0 on final failure outside the loop
      // Use the error message from the caught error, which might be the user-friendly one from the loop or an initial setup error
      onProgress(1.0, null, `Error: ${error.message}`);
      throw error; // Re-throw the error
  } finally {
      // Optional: Cleanup logic if needed
  }
};


/**
 * Edits an image using Gemini based on a natural language prompt (mask-free).
 * Uploads the result to Supabase and returns the public URL.
 *
 * @param {string} prompt - The text prompt describing the edit.
 * @param {string|File} inputImage - The input image URL or File object to edit.
 * @param {function} [onProgress] - Optional callback for progress updates (value 0-1, output, message).
 * @returns {Promise<{publicUrl: string, imageGenPrompt: string}>} - An object containing the public Supabase URL and the prompt used.
 */
export const runGeminiMaskFreeEdit = async (prompt, inputImage, onProgress = () => {}) => {
  console.log("Starting Gemini Mask-Free Edit...");
  onProgress(0.1, null, "Preparing input..."); // Initial progress

  let generatedImageUrl = null;
  let lastError = null;

  try {
    // --- Construct the Editing Prompt ---
    // Wrap the user's prompt with specific instructions for the model
    const editInstruction = `Edit the following image and "${prompt}"`;
    console.log("Constructed Edit Instruction:", editInstruction);

    const contentParts = [{ text: editInstruction }];

    // --- Process Input Image (Similar to runGeminiImageGeneration, no resizing here) ---
    let inputMimeType = null;
    let inputBase64Data = null;

    if (inputImage) {
      onProgress(0.2, null, "Processing input image...");
      try {
        let imageBlob;
        if (inputImage instanceof File) {
          console.log("Processing File input...");
          imageBlob = inputImage;
          inputMimeType = inputImage.type;
        } else if (typeof inputImage === 'string') {
          console.log("Processing string input (URL or data URL)...");
          const { blob: fetchedBlob, mimeType: fetchedMimeType } = await urlToBlob(inputImage);
          imageBlob = fetchedBlob;
          inputMimeType = fetchedMimeType;
        } else {
          throw new Error('Invalid input image type. Must be File or URL string.');
        }

        if (!imageBlob) {
            throw new Error('Failed to get image blob.');
        }

        console.log(`Input image details - MimeType: ${inputMimeType}, Size: ${imageBlob.size} bytes`);
        inputBase64Data = await blobToBase64(imageBlob);
        contentParts.push({
          inlineData: {
            mimeType: inputMimeType,
            data: inputBase64Data,
          },
        });
        console.log("Input image processed successfully for editing.");
        onProgress(0.4, null, "Input image ready.");
      } catch (error) {
        console.error("Error processing input image for editing:", error);
        throw new Error(`Failed to process input image: ${error.message}`);
      }
    } else {
       throw new Error("Input image is required for mask-free editing.");
    }

    // --- Call Gemini API with Retries and Timeout ---
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      console.log(`Gemini Mask-Free Edit Attempt ${attempt}/${MAX_RETRIES}...`);
      const attemptProgressStart = 0.5 + (0.4 / MAX_RETRIES) * (attempt - 1);
      const attemptProgressEnd = attemptProgressStart + (0.4 / MAX_RETRIES);

      try {
        onProgress(attemptProgressStart, null, `Editing image (Attempt ${attempt})...`);
        console.log("Sending request to Gemini API with edit instruction and image.");

        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`Timeout: Edit exceeded ${GENERATION_TIMEOUT_MS / 1000} seconds.`)), GENERATION_TIMEOUT_MS)
        );

        // Use the same imageModel as generation for now, assuming it handles editing prompts
        const generationPromise = imageModel.generateContent({
          contents: [{ role: "user", parts: contentParts }],
          generationConfig: {
            responseModalities: ['Text', 'Image'],
            temperature: 0.5, // Slightly lower temp might be better for edits
            topK: 1,
            topP: 0.95,
          },
          // Safety settings inherited from model initialization
        });

        const result = await Promise.race([generationPromise, timeoutPromise]);

        onProgress(attemptProgressEnd * 0.8, null, "Processing Gemini response...");
        console.log("Received response from Gemini API for edit.");

        const response = result.response;
        if (!response || !response.candidates || response.candidates.length === 0) {
          console.error("Invalid response structure from Gemini edit:", response);
          throw new Error("Invalid or empty response received from Gemini API during edit.");
        }

        const candidate = response.candidates[0];
        // Check safety ratings if needed
        // if (candidate.finishReason && candidate.finishReason !== 'STOP') { ... }

        if (!candidate.content || !candidate.content.parts || candidate.content.parts.length === 0) {
          console.error("No content parts found in Gemini edit response:", candidate);
          throw new Error("No content parts found in the Gemini edit response.");
        }

        const imagePart = candidate.content.parts.find(part => part.inlineData && part.inlineData.data);

        if (!imagePart) {
          const textPart = candidate.content.parts.find(part => part.text);
          const errorMessage = textPart ? `Gemini Error during edit: ${textPart.text}` : "No image data found in the Gemini edit response.";
          console.error(errorMessage, candidate.content.parts);
          // Check for safety blocks specifically
           if (candidate.finishReason === 'SAFETY') {
             const safetyMessage = candidate.safetyRatings?.map(r => `${r.category}: ${r.probability}`).join(', ') || 'Safety block details unavailable.';
             throw new Error(`Görsel düzenleme güvenlik filtreleri tarafından engellendi. Lütfen isteğinizi değiştirin. (${safetyMessage})`);
           }
          throw new Error(errorMessage);
        }

        const generatedBase64 = imagePart.inlineData.data;
        const generatedMimeType = imagePart.inlineData.mimeType || 'image/webp'; // Default to webp

        console.log(`Edited image mime type: ${generatedMimeType}`);
        onProgress(attemptProgressEnd * 0.9, null, "Uploading edited image...");

        // --- Convert Base64 to Blob ---
        const generatedBlob = base64ToBlob(generatedBase64, generatedMimeType);
        if (!generatedBlob) {
          throw new Error("Failed to convert edited base64 to Blob.");
        }

        // --- Ensure Blob is WebP for upload ---
        let uploadBlob = generatedBlob;
        let uploadMimeType = generatedMimeType;
        const desiredUploadFormat = 'image/webp';

        if (generatedMimeType !== desiredUploadFormat && generatedBlob) {
          console.log(`Converting edited ${generatedMimeType} to ${desiredUploadFormat}...`);
          let objectUrl = null;
          try {
            objectUrl = URL.createObjectURL(generatedBlob);
            const webpBlob = await resizeImage(objectUrl, 4096, desiredUploadFormat, 0.9);
            uploadBlob = webpBlob;
            uploadMimeType = desiredUploadFormat;
            console.log("Conversion to webp successful.");
          } catch (conversionError) {
            console.warn(`Failed to convert edited image to ${desiredUploadFormat}. Uploading as ${generatedMimeType}. Error: ${conversionError.message}`);
            uploadBlob = generatedBlob;
            uploadMimeType = generatedMimeType;
          } finally {
            if (objectUrl) {
              URL.revokeObjectURL(objectUrl);
            }
          }
        }

        // --- Upload to Supabase ---
        const { data: { user } } = await supabase.auth.getUser();
        const userId = user?.id || 'anonymous';
        // Include 'edit' in the filename
        const fileName = `${userId}/gemini-edit-${Date.now()}.webp`;

        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('generated-images') // Use the same bucket for now
          .upload(fileName, uploadBlob, {
            contentType: uploadMimeType,
            cacheControl: '3600',
            upsert: false,
          });

        if (uploadError) {
          console.error("Supabase upload error (edit):", uploadError);
          throw new Error(`Failed to upload edited image: ${uploadError.message}`);
        }

        console.log("Edited image uploaded to Supabase:", uploadData.path);

        // --- Get Public URL ---
        const publicUrl = getPublicUrl('generated-images', fileName);
        if (!publicUrl) {
          throw new Error("Failed to get public URL for the edited image.");
        }

        console.log("Edited image public URL:", publicUrl);
        generatedImageUrl = publicUrl;
        // Pass back the original user prompt for reference if needed
        onProgress(1.0, { publicUrl, imageGenPrompt: prompt }, "Edit complete!");
        return { publicUrl, imageGenPrompt: prompt }; // Success

      } catch (error) {
        lastError = error;
        console.error(`Gemini Mask-Free Edit Attempt ${attempt} failed:`, error);

        if (attempt < MAX_RETRIES) {
          onProgress(attemptProgressEnd, null, `Attempt ${attempt} failed. Retrying... (${error.message})`);
          // await new Promise(resolve => setTimeout(resolve, 1000)); // Optional delay
        } else {
          console.error("Max retries reached for edit. Edit failed.");
          onProgress(1.0, null, `Error: Edit failed after ${MAX_RETRIES} attempts. Please try again.`);
          throw new Error(`Görsel düzenleme ${MAX_RETRIES} denemeden sonra başarısız oldu. Lütfen tekrar deneyin veya farklı bir komut kullanın. (Son hata: ${lastError.message})`);
        }
      }
    }
  } catch (error) {
      console.error('Gemini Mask-Free Edit Error (Overall):', error);
      onProgress(1.0, null, `Error: ${error.message}`);
      throw error;
  } finally {
      // Cleanup logic if needed
  }
};
